/**
 * EPX 2026-08-13 — show the chosen colour in the product title.
 *
 * Founder brief: once a colour is picked the product should read
 * "Aluminium Slab Guard (Red)". Before anything is picked it stays as the plain
 * product name.
 *
 * "Picked" comes from data-colour on the snippet's element, which Liquid fills
 * from `selected_variant` — the same signal the colour-filtered gallery in
 * snippets/product-media-gallery-content.liquid uses. A URL check was tried
 * first and was wrong: Horizon fetches the section with the variant but does
 * not always push ?variant= into the browser URL, so the heading disagreed with
 * the photos. One server-side signal keeps them in lockstep.
 *
 * Listeners are delegated off `document` and a MutationObserver re-applies after
 * Horizon morphs the product section, which otherwise resets the heading.
 */
(function () {
  const data = () => document.querySelector('[data-epx-variant-title]');

  /**
   * The product heading, found by matching the base title rather than by
   * position — `document.querySelector('.shopify-section')` returns the HEADER
   * section, which has no h1, so a scoped lookup silently found nothing.
   * Matching on the title also makes re-application idempotent, since an
   * already-suffixed heading still starts with the base title.
   */
  function heading() {
    const cfg = data();
    const base = cfg ? cfg.dataset.baseTitle || '' : '';
    if (!base) return null;
    return [...document.querySelectorAll('h1')].find((h) => h.textContent.trim().startsWith(base)) || null;
  }

  const slugify = (v) => v.toLowerCase().replace(/\s*\/\s*/g, '-').replace(/\s+/g, '-');

  /**
   * Read the colour off the GALLERY rather than any variant state.
   *
   * Two earlier attempts failed: ?variant= is not always pushed to the browser
   * URL, and `selected_variant` is simply not exposed to custom-liquid blocks
   * (it is blank there even though the media-gallery block sees it). The gallery
   * is already colour-filtered server-side, so whatever it is showing IS the
   * chosen colour — using it as the source of truth makes the heading agree with
   * the photos by construction.
   */
  function chosenColour() {
    const gallery = document.querySelector('media-gallery');
    const img = gallery && gallery.querySelector('slideshow-slide img');
    const src = img ? img.getAttribute('src') || '' : '';
    const m = src.match(/espionage-(?:deck-box|slab-guard)-(.+?)-\d+\.jpg/);
    if (!m || m[1] === 'all-colours') return null;

    const match = [...document.querySelectorAll('variant-picker fieldset[data-fieldset-index="0"] input')]
      .find((i) => slugify(i.value) === m[1]);
    return match ? match.value : null;
  }

  /**
   * Name the currently highlighted swatch in the legend ("Colour: Red").
   *
   * The CSS swatches hide the button label text, so with ten similar circles
   * the colour name appeared nowhere on the page except the heading — and the
   * heading only names a colour once one has actually been chosen. This reads
   * the checked radio rather than the gallery, so it always describes the
   * control the customer is looking at.
   */
  function applyLegend() {
    const fieldset = document.querySelector('variant-picker fieldset[data-fieldset-index="0"]');
    if (!fieldset) return;
    const legend = fieldset.querySelector('legend');
    const checked = fieldset.querySelector('input:checked');
    if (!legend || !checked) return;

    if (!legend.dataset.epxBase) legend.dataset.epxBase = legend.textContent.trim().split(':')[0].trim();
    const wanted = `${legend.dataset.epxBase}: ${checked.value}`;
    if (legend.textContent.trim() !== wanted) legend.textContent = wanted;
  }

  function apply() {
    applyLegend();

    const cfg = data();
    const h1 = heading();
    if (!cfg || !(h1 instanceof HTMLElement)) return;

    const base = cfg.dataset.baseTitle || '';
    if (!base) return;

    const colour = chosenColour();
    const wanted = colour ? `${base} (${colour})` : base;
    if (h1.textContent.trim() !== wanted) h1.textContent = wanted;
  }

  let running = false;
  function watch() {
    const host = document.querySelector('product-information') || document.body;
    new MutationObserver(() => {
      if (running) return;
      running = true;
      try {
        apply();
      } finally {
        running = false;
      }
    }).observe(host, { childList: true, subtree: true });
  }

  document.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.type === 'radio') setTimeout(apply, 60);
  });
  window.addEventListener('popstate', () => setTimeout(apply, 60));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { apply(); watch(); }, { once: true });
  } else {
    apply();
    watch();
  }
})();
